/**
 * The reports list: every template the caller can see, split into what they
 * own, what was shared with them, and what is published to the tenant. The tab
 * and search live in the URL so a filtered list is a link.
 */
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { apiRequest } from '../../lib/apiClient.js';
import { queryKeys } from '../../lib/queryKeys.js';

const reportTemplateSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    owner: z.object({ id: z.string(), name: z.string().nullable() }),
    visibility: z.enum(['private', 'tenant']),
    version: z.number(),
    template: z
      .object({
        units: z
          .object({
            kind: z.enum(['filters', 'unit_list']),
            billingStatus: z.string().nullable(),
            fleetStatus: z.string().nullable(),
            unitComponent: z.string().nullable(),
          })
          .passthrough(),
        sections: z.array(z.object({ kind: z.string(), title: z.string() }).passthrough()),
      })
      .passthrough(),
  })
  .passthrough();
export type ReportTemplateRow = z.infer<typeof reportTemplateSchema>;

export const REPORT_TABS = [
  { id: 'mine', label: 'Mine' },
  { id: 'shared', label: 'Shared with me' },
  { id: 'published', label: 'Published' },
] as const;
export type ReportTab = (typeof REPORT_TABS)[number]['id'];

/** Mine includes your published reports; Published includes everyone's. */
export const inTab = (template: ReportTemplateRow, meId: string, tab: ReportTab): boolean =>
  tab === 'mine'
    ? template.owner.id === meId
    : tab === 'published'
      ? template.visibility === 'tenant'
      : template.owner.id !== meId && template.visibility === 'private';

/** A short line of what a report covers, from its first few section titles. */
export const coversLine = (template: ReportTemplateRow): string => {
  const titles = template.template.sections
    .filter((s) => s.kind !== 'text')
    .slice(0, 3)
    .map((s, i) => (i === 0 ? s.title : s.title.toLowerCase()));
  return titles.length > 1
    ? `${titles.slice(0, -1).join(', ')} and ${titles.at(-1) ?? ''}`
    : (titles[0] ?? '');
};

/** Every template the caller can see; shared with the workflow builder's report picker. */
export const reportTemplatesQuery = {
  queryKey: queryKeys.reportTemplates(),
  queryFn: async () =>
    z.array(reportTemplateSchema).parse(await apiRequest<unknown>('/api/report-templates')),
};

export const useReportsPageLogic = (meId: string | undefined) => {
  const templates = useQuery({ ...reportTemplatesQuery, enabled: Boolean(meId) });

  const [params, setParams] = useSearchParams();
  const tab = REPORT_TABS.find((t) => t.id === params.get('tab'))?.id ?? 'mine';
  const search = params.get('q') ?? '';
  // Defaults stay out of the URL so a plain /reports link stays plain.
  const setParam = (key: string, value: string, defaultValue: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === defaultValue) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );

  const needle = search.trim().toLowerCase();
  const rows = (templates.data ?? []).filter(
    (t) => inTab(t, meId ?? '', tab) && (!needle || t.name.toLowerCase().includes(needle)),
  );

  return {
    isLoading: templates.isLoading,
    error: templates.error,
    total: templates.data?.length ?? 0,
    rows,
    tab,
    setTab: (next: ReportTab) => setParam('tab', next, 'mine'),
    search,
    setSearch: (next: string) => setParam('q', next, ''),
  };
};
